import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { axiosInstance } from '../../axios/axiosHandler';
import { EndPoints } from '../../axios/endPoints';
import { authHeader } from '../auth-slice/auth-slice';

export interface User {
  userID: number,
  username: string,
  email: string,
  role: string,
  isEnabled: boolean,
  isConfirmed: boolean,
  isCurrentUser: boolean
}

export const getUsers = createAsyncThunk(
  'GetUsers',
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        EndPoints.USER_MANAGEMENT_GET_USERS,
        authHeader()
      );
      return await  response.data as User[];
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

interface AddNewUserBody {
  email: string,
  role: string,
  changePasswordUrl: string
}

export const addNewUser = createAsyncThunk(
  'AddNewUser',
  async (body: AddNewUserBody, thunkAPI) => {
    try {
      const response = await axiosInstance.post(
        EndPoints.USER_MANAGEMENT_ADD_USER,
        body,
        authHeader()
      );
      return await  response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

interface EnableUserProps {
  email: string,
  enable: boolean
}

export const enableUser = createAsyncThunk(
  'EnableUser',
  async (body: EnableUserProps, thunkAPI) => {
    try {
      const response = await axiosInstance.post(
        EndPoints.USER_MANAGEMENT_ENABLE_USER,
        body,
        authHeader()
      );
      return await  response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

interface ChangeRoleProps {
  email: string,
  newRole: string
}

export const changeRole = createAsyncThunk(
  'ChangeRole',
  async (body: ChangeRoleProps, thunkAPI) => {
    try {
      const response = await axiosInstance.post(
        EndPoints.USER_MANAGEMENT_CHANGE_ROLE,
        body,
        authHeader()
      );
      return await  response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);
export const usermanagementSlice = createSlice({
    name: 'usermanagement',
    initialState: {
      loadingUsers: false as boolean,
      users: [] as User[],
      reloadUsers: 0 as number,
      addNewUserDialog: false as boolean,
      openLoader: false as boolean,
      enableUserDialog: false as boolean,
      enableUserBodyParams: null as { email: string, enable: boolean } | null,
      changeRoleDialog: false as boolean,
      changeRoleBodyParams: null as { email: string, role: string } | null,
    },
    reducers: {
      handleToggleAddNewUserDialog: (state, action) => {
        state.addNewUserDialog = action.payload;
      },
      handleToggleChangeRoleDialog: (state, action) => {
        state.changeRoleDialog = action.payload.open;
        state.changeRoleBodyParams = action.payload.body;
      },
      handleToggleEnableUserDialog: (state, action) => {
        state.enableUserDialog = action.payload.open;
        state.enableUserBodyParams = action.payload.body;
      }
    },
    extraReducers: (builder) => {
      builder.addCase(getUsers.pending, (state, action) => { 
        state.loadingUsers = true;
      });
      builder.addCase(getUsers.fulfilled, (state, action) => { 
        state.loadingUsers = false;
        state.users = action.payload;
      });
      builder.addCase(getUsers.rejected, (state, action) => { 
        state.loadingUsers = false;
        state.users = [];
      });

      builder.addCase(addNewUser.pending, (state, action) => { 
        state.openLoader = true;
        state.addNewUserDialog = false;
      });
      builder.addCase(addNewUser.fulfilled, (state, action) => { 
        state.openLoader = false;
        state.reloadUsers = state.reloadUsers + 1;
      });
      builder.addCase(addNewUser.rejected, (state, action) => { 
        state.openLoader = false;
        state.reloadUsers = state.reloadUsers + 1;
      });

      builder.addCase(enableUser.pending, (state, action) => { 
        state.openLoader = true;
        state.enableUserDialog = false;
        state.enableUserBodyParams = null;
      });
      builder.addCase(enableUser.fulfilled, (state, action) => { 
        state.openLoader = false;
        state.reloadUsers = state.reloadUsers + 1;
      });
      builder.addCase(enableUser.rejected, (state, action) => { 
        state.openLoader = false;
        state.reloadUsers = state.reloadUsers + 1;
      });
      
      builder.addCase(changeRole.pending, (state, action) => { 
        state.openLoader = true;
        state.changeRoleDialog = false;
        state.changeRoleBodyParams = null;
      });
      builder.addCase(changeRole.fulfilled, (state, action) => { 
        state.openLoader = false;
        state.reloadUsers = state.reloadUsers + 1;
      });
      builder.addCase(changeRole.rejected, (state, action) => { 
        state.openLoader = false;
        state.reloadUsers = state.reloadUsers + 1;
      });
    },
});

export const { 
  handleToggleChangeRoleDialog,
  handleToggleAddNewUserDialog, 
  handleToggleEnableUserDialog 
} = usermanagementSlice.actions;

export const usermanagementSelector = (state: RootState) => state.usermanagement;
